// Background bulk uploads (REACT_APP_BULK_INSERT_ASYNC=true): submits files to the API's job queue and keeps
// the account's uploads up to date. State lives on the server (GET jobs/current), so a refresh, another tab or
// another device shows the same uploads; nothing is kept in localStorage.
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { responseData } from "../../../utils/helpers/helper";

const BASE = () => `${process.env.REACT_APP_API_URL}market/bulkinsert`;
const CONFIG = { headers: { Accept: "application/json" } };
const RUNNING_POLL_MS = 3000;
const QUEUED_POLL_MS = 10000;
const MAX_BACKOFF_MS = 30000;

export const EXPIRED_RESULT_MESSAGE = "This result is no longer available. Results are kept for 7 days; the email has the summary.";
export const CANCEL_TOO_LATE_MESSAGE = "This upload has already started and can't be cancelled.";

// A new id per upload click: a repeated request (double click, retry) returns the same job
export const newRequestId = () => (typeof crypto !== "undefined" && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export default function useBulkUploadJobs({ enabled, onResult, onFailed, onUnauthorized }) {
  const [current, setCurrent] = useState({ active: null, queued: [], unacknowledged: [], more: false });
  const [reconnecting, setReconnecting] = useState(false);
  const [message, setMessage] = useState("");
  // false when the API answers 404 for the queue (background uploads switched off there, or an older API): the
  // page then uses the one-request upload
  const [available, setAvailable] = useState(true);
  const myJobs = useRef(new Set()); // submitted from this page: their result opens by itself
  const timer = useRef(null);
  const failures = useRef(0);
  const stopped = useRef(false);
  const opening = useRef(new Set());
  const handlers = useRef({ onResult, onFailed, onUnauthorized });
  handlers.current = { onResult, onFailed, onUnauthorized };

  // true when the error was a 401 (handled by the page: logout dialog or back to login)
  const handleUnauthorized = (error) => {
    if (error?.response?.status !== 401) return false;
    stopped.current = true;
    clearTimeout(timer.current);
    handlers.current.onUnauthorized?.(error.response);
    return true;
  };

  // true when the queue routes answer 404 (background uploads off on the API): stop asking
  const handleUnavailable = (error) => {
    if (error?.response?.status !== 404) return false;
    stopped.current = true;
    clearTimeout(timer.current);
    setReconnecting(false);
    setAvailable(false);
    return true;
  };

  const openResult = useCallback(async (jobId) => {
    if (opening.current.has(jobId)) return;
    opening.current.add(jobId);
    try {
      const res = await axios.get(`${BASE()}/jobs/${jobId}/result`, CONFIG);
      const result = responseData(res);
      if (result?.state === "failed") {
        handlers.current.onFailed?.({ status: result.status, data: result.body }, { jobId });
      } else {
        handlers.current.onResult?.(result.body, { updateAirline: Boolean(result.updateAirline), state: result.state });
      }
      myJobs.current.delete(jobId);
      await axios.post(`${BASE()}/jobs/${jobId}/ack`, {}, CONFIG);
    } catch (error) {
      if (handleUnauthorized(error)) return;
      if (error?.response?.status === 404) setMessage(EXPIRED_RESULT_MESSAGE);
      else setMessage(responseData(error?.response)?.message || "The result could not be loaded. Please try again.");
    } finally {
      opening.current.delete(jobId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!enabled || stopped.current) return;
    clearTimeout(timer.current);
    let next = null;
    try {
      const data = responseData(await axios.get(`${BASE()}/jobs/current`, CONFIG)) || {};
      const state = {
        active: data.active || null,
        queued: data.queued || [],
        unacknowledged: data.unacknowledged || [],
        more: Boolean(data.more),
      };
      setCurrent(state);
      setReconnecting(false);
      failures.current = 0;
      // This user's finished upload opens by itself with the usual report or error popup, also when the page was
      // closed during the upload (one at a time: the next one opens on the next visit)
      const ownResult = [...state.unacknowledged].reverse().find((job) => job.submittedByMe || myJobs.current.has(job.jobId));
      if (ownResult) openResult(ownResult.jobId);
      // Running or next to start (the worker picks it up within seconds): poll often so the progress shows
      // straight away; with other uploads ahead, less often
      const mine = state.queued.filter((job) => job.submittedByMe)
      if (state.active?.submittedByMe || mine.some((job) => !(job.queuePosition > 0))) next = RUNNING_POLL_MS;
      else if (mine.length) next = QUEUED_POLL_MS;
    } catch (error) {
      if (handleUnauthorized(error) || handleUnavailable(error)) return;
      failures.current += 1;
      setReconnecting(true);
      next = Math.min(RUNNING_POLL_MS * 2 ** (failures.current - 1), MAX_BACKOFF_MS);
    }
    if (next && !stopped.current && document.visibilityState !== "hidden") timer.current = setTimeout(load, next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, openResult]);

  useEffect(() => {
    if (!enabled) return undefined;
    stopped.current = false;
    load();
    const refresh = () => { if (document.visibilityState !== "hidden") load(); };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("online", refresh);
    return () => {
      stopped.current = true;
      clearTimeout(timer.current);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("online", refresh);
    };
  }, [enabled, load]);

  // Queues a file; errors are thrown to the caller (same messages and dialogs as the one-request upload). A 404
  // (background uploads off on the API) also marks the queue unavailable, so the page sends the file the old way.
  const submit = useCallback(async (formData) => {
    formData.append("clientRequestId", newRequestId());
    let res;
    try {
      res = await axios.post(`${BASE()}/async`, formData, CONFIG);
    } catch (error) {
      handleUnavailable(error);
      throw error;
    }
    const data = responseData(res);
    if (data?.jobId) myJobs.current.add(data.jobId);
    setMessage("");
    await load();
    return data;
  }, [load]);

  const cancel = useCallback(async (jobId) => {
    try {
      await axios.post(`${BASE()}/jobs/${jobId}/cancel`, {}, CONFIG);
      myJobs.current.delete(jobId);
    } catch (error) {
      if (handleUnauthorized(error)) return;
      setMessage(error?.response?.status === 409 ? CANCEL_TOO_LATE_MESSAGE : (responseData(error?.response)?.message || "The upload could not be cancelled."));
    }
    await load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // An upload of this user is running or waiting: the page waits for it (progress screen), as it waits for the
  // one-request upload. Uploads of other users of the account do not block this page.
  const inProgress = Boolean(current.active?.submittedByMe) || current.queued.some((job) => job.submittedByMe);

  return {
    current, inProgress, available, reconnecting, message, setMessage, submit, cancel, openResult, reload: load,
  };
}
